import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { CartDrawer } from "./CartDrawer";
import LanguageToggle from "./LanguageToggle";
import { useLanguage } from "@/i18n/LanguageContext";
import type { TranslationKey } from "@/i18n/translations";

const navLinks: { to: string; labelKey: TranslationKey }[] = [
  { to: "/", labelKey: "nav.home" },
  { to: "/services", labelKey: "nav.services" },
  { to: "/founder-readiness", labelKey: "nav.founderReadiness" },
  { to: "/capital-raising", labelKey: "nav.capitalRaising" },
  { to: "/founder-laws", labelKey: "nav.founderLaws" },
];

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    return () => sub.subscription.unsubscribe();
  }, []);

  const accountLabel = session ? t("nav.governance") : t("nav.signIn");
  const accountClass = `text-sm font-medium transition-colors link-underline font-sans ${
    isScrolled ? "text-foreground/80 hover:text-foreground" : "text-white/80 hover:text-white"
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-lg shadow-sm border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className={`text-xl font-bold tracking-tight transition-colors font-sans ${
              isScrolled ? "text-foreground" : "text-white"
            } hover:text-accent`}
          >
            Peter Dingle
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors link-underline font-sans ${
                  isScrolled ? "text-foreground/80 hover:text-foreground" : "text-white/80 hover:text-white"
                }`}
              >
                {t(link.labelKey)}
              </Link>
            ))}
            <Link to="/governance" className={accountClass}>
              {accountLabel}
            </Link>
            <LanguageToggle light={!isScrolled} />
            <Button
              variant={isScrolled ? "accent" : "hero"}
              size="sm"
              onClick={() => window.open("https://calendar.app.google/smFTnDe7izrtAPVg6", "_blank")}
              className="font-sans"
            >
              {t("nav.bookCall")}
            </Button>
            <CartDrawer />
          </div>

          <div className="md:hidden flex items-center gap-2">
            <LanguageToggle light={!isScrolled} />
            <CartDrawer />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("nav.openMenu")}
                  className={isScrolled ? "text-foreground" : "text-white hover:bg-white/10 hover:text-white"}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetTitle className="font-sans text-base mb-6">{t("nav.menu")}</SheetTitle>
                <div className="flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="text-base font-medium font-sans py-3 px-2 rounded-md text-foreground/90 hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      {t(link.labelKey)}
                    </Link>
                  ))}
                  <Link
                    to="/governance"
                    onClick={() => setMobileOpen(false)}
                    className="text-base font-medium font-sans py-3 px-2 rounded-md text-foreground/90 hover:text-foreground hover:bg-secondary transition-colors border-t border-border/60 mt-2"
                  >
                    {accountLabel}
                  </Link>
                  {session && (
                    <button
                      type="button"
                      onClick={() => {
                        setMobileOpen(false);
                        supabase.auth.signOut();
                      }}
                      className="text-base font-medium font-sans py-3 px-2 rounded-md text-left text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      {t("nav.signOut")}
                    </button>
                  )}
                  <div className="mt-4">
                    <LanguageToggle />
                  </div>
                  <Button
                    variant="accent"
                    size="sm"
                    onClick={() => {
                      setMobileOpen(false);
                      window.open("https://calendar.app.google/smFTnDe7izrtAPVg6", "_blank");
                    }}
                    className="font-sans mt-4"
                  >
                    {t("nav.bookCall")}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
